import React, { useState, useEffect, useMemo, useRef } from "react";
import { Box, Container, Typography, TextField, Button, Paper, LinearProgress } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import bkstApi from "../../common/component/api";
import { useLocation } from "react-router-dom";
import ProductTile from "../component/ProductTile";
import { getSoftBackgroundColor, generateRandomPattern } from "../../common/component/util";
import { useParams } from "react-router-dom";
import html2canvas from "html2canvas";

export default function Marketing(props) {
  const { siteId } = useParams();
  let location = useLocation();
  const paperRef = useRef(null);

  const [siteConfig, setSiteConfig] = useState("");
  const [subject, setSubject] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [copy, setCopy] = useState("");
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    bkstApi.get(`/admin/site/${siteId}/siteConfig`).then((res) => {
      setSiteConfig(res.data.siteConfig);
      setLoading(false);
    });
  }, [location.state]);

  const backgroundPattern = useMemo(() => {
    if (!siteConfig?.theme?.palette?.primary) return null;
    return generateRandomPattern(siteConfig.theme.palette.primary);
  }, [siteConfig?.theme?.palette?.primary]);

  if (!siteConfig) {
    return <LinearProgress />;
  } else {
    const themeColor = siteConfig?.theme?.palette?.primary;
    const softBg = themeColor ? getSoftBackgroundColor(themeColor) : "#f0f0f0";

    const handleDownloadImage = async () => {
      if (!paperRef.current) return;

      setDownloading(true);
      try {
        // Wait for images to load
        const images = paperRef.current.querySelectorAll("img");
        await Promise.all(
          Array.from(images).map(
            (img) =>
              new Promise((resolve) => {
                if (img.complete) {
                  resolve();
                } else {
                  img.onload = resolve;
                  img.onerror = resolve;
                  setTimeout(resolve, 5000);
                }
              })
          )
        );

        await new Promise((resolve) => setTimeout(resolve, 500));

        const canvas = await html2canvas(paperRef.current, {
          backgroundColor: softBg,
          scale: 2,
          logging: false,
          useCORS: true,
          allowTaint: true,
          imageTimeout: 30000,
        });

        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `campaign-flyer-${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }
        }, "image/png");
      } catch (err) {
        console.error("Error generating image:", err);
      } finally {
        setDownloading(false);
      }
    };

    return (
      <Container maxWidth="sm">
        <Box my={4}>
          <Typography variant="h1" gutterBottom>
            Campaign Flyer
          </Typography>

          <Paper elevation={1} sx={{ p: 4 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <TextField fullWidth label="Title" value={subject} onChange={(e) => setSubject(e.target.value)} />
              <TextField fullWidth label="Copy (Optional)" value={copy} onChange={(e) => setCopy(e.target.value)} rows={4} multiline />
            </Box>
          </Paper>

          <Box my={5}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="overline" color="text.secondary">
                Theme Color: {themeColor} | Background: {softBg}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadImage}
                disabled={downloading}
              >
                {downloading ? "Downloading..." : "Download"}
              </Button>
            </Box>
            <Paper
              ref={paperRef}
              elevation={1}
              sx={{
                p: 4,
                backgroundColor: softBg,
                backgroundImage: backgroundPattern,
                backgroundSize: "300px 300px",
                backgroundRepeat: "repeat",
                position: "relative"
              }}
            >
              <Box textAlign="center" mb={4}>
                {subject && (
                  <Typography variant="h1" sx={{ color: themeColor, fontFamily: '"Courier New", Courier, monospace' }} gutterBottom>
                    {subject}
                  </Typography>
                )}
                {copy && (
                  <Typography variant="subtitle1" color="text.secondary">
                    {copy}
                  </Typography>
                )}
              </Box>
              {location.state.items &&
                location.state.items.map((o, index) => <ProductTile product={o} themeColor={themeColor} index={index} key={index} />)}
              <Box mt={4} textAlign="center">
                <Button size="large" variant="contained" color="primary" sx={{ backgroundColor: themeColor }}>
                  Show More..
                </Button>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Container>
    );
  }
}
